import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PostPhoto, PostStatus, PostTagsDelta, PostType } from '@reditelji/shared';
import { mergeSaved } from './cache';
import { api, ApiRequestError } from './client';
import type { PostWindow, Sector, Shift } from './types';

export interface PostRow {
  id: string;
  sectorId: string;
  code: string;
  micro: string;
  type: PostType;
  urgent: boolean;
  status: PostStatus;
  lat: number | null;
  lng: number | null;
  instructions: string;
  /** English for the briefing (SPEC §1.3); see `instructionsEnFresh`. */
  instructionsEn: string;
  /** Written or edited by hand, so a change to the Slovenian does not replace it. */
  instructionsEnManual: boolean;
  /** Made from the Slovenian as it stands now. */
  instructionsEnFresh: boolean;
  /** Replaces the title the briefing card derives from the micro-location. */
  briefingTitle: string;
  gear: string[];
  leadName: string;
  leadPhone: string;
  createdAt: string;
  updatedAt: string;
  windows: PostWindow[];
  /** Metadata only; the bytes come from /api/v1/photos/:key. */
  photos: PostPhoto[];
  /** Labels this coordinator can see — shared ones and their own private ones. */
  tagIds: string[];
}

export function usePosts() {
  return useQuery({ queryKey: ['posts'], queryFn: () => api<PostRow[]>('/posts') });
}

export function useSectors() {
  return useQuery({ queryKey: ['sectors'], queryFn: () => api<Sector[]>('/sectors') });
}

/** All shifts across days except called-off ones (Mesta coverage + day cards). */
export function useAllShifts() {
  return useQuery({ queryKey: ['shifts', 'all'], queryFn: () => api<Shift[]>('/shifts') });
}

/** Shared by every Mesta mutation, photos included. */
export function notifyApiError(err: Error) {
  notifications.show({
    color: 'red',
    title: 'Napaka',
    message:
      err instanceof ApiRequestError && err.status === 409
        ? 'Podatek je medtem spremenil nekdo drug. Osveži in poskusi znova.'
        : err.message,
  });
}

export function usePostMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['posts'] });
    void qc.invalidateQueries({ queryKey: ['shifts'] });
    void qc.invalidateQueries({ queryKey: ['timeline'] });
    void qc.invalidateQueries({ queryKey: ['overview'] });
    void qc.invalidateQueries({ queryKey: ['changelog'] });
  };
  const common = { onError: notifyApiError, onSettled: invalidate };

  return {
    create: useMutation({
      mutationFn: (input: Record<string, unknown>) =>
        api<PostRow>('/posts', { method: 'POST', body: JSON.stringify(input) }),
      ...common,
    }),
    update: useMutation({
      mutationFn: ({ id, ...patch }: { id: string; updatedAt: string } & Record<string, unknown>) =>
        api<PostRow>(`/posts/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
      // A posts fetch still on its way read the row before this write, and
      // landing after it would put the old post back under the editor.
      onMutate: () => qc.cancelQueries({ queryKey: ['posts'] }),
      // The editor is keyed on `updatedAt`, so writing the answer in place
      // remounts it on the saved post at once. The body sends `location`; the
      // row stores it as `lat` and `lng`.
      onSuccess: (saved, { id, ...patch }) =>
        mergeSaved<PostRow>(qc, ['posts'], id, saved, [
          ...Object.keys(patch),
          ...('location' in patch ? ['lat', 'lng'] : []),
          // Saving either language can change what the English is and whether it is current.
          ...('instructions' in patch || 'instructionsEn' in patch
            ? ['instructionsEn', 'instructionsEnManual', 'instructionsEnFresh']
            : []),
        ]),
      ...common,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api<void>(`/posts/${id}`, { method: 'DELETE' }),
      ...common,
    }),
    setWindows: useMutation({
      mutationFn: (input: {
        postId: string;
        eventDayId: string;
        windows: { fromMin: number; toMin: number }[];
      }) =>
        api<PostWindow[]>(`/posts/${input.postId}/windows`, {
          method: 'PUT',
          body: JSON.stringify({ eventDayId: input.eventDayId, windows: input.windows }),
        }),
      // That day's windows, as stored, in place of the ones the list holds.
      onSuccess: (created, { postId, eventDayId }) =>
        qc.setQueryData<PostRow[]>(['posts'], (rows) =>
          rows?.map((p) =>
            p.id === postId
              ? { ...p, windows: [...p.windows.filter((w) => w.eventDayId !== eventDayId), ...created] }
              : p,
          ),
        ),
      ...common,
    }),
  };
}

/**
 * Add or remove labels on one or many posts. The answer goes straight into the
 * cached list: a label write leaves the post's `updatedAt` as it was, so the
 * open editor takes the new labels without remounting over unsaved edits.
 */
export function usePostTagsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PostTagsDelta) =>
      api<{ posts: { id: string; tagIds: string[] }[]; skipped: string[] }>('/posts/tags', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (res) => {
      const next = new Map(res.posts.map((p) => [p.id, p.tagIds]));
      qc.setQueryData<PostRow[]>(['posts'], (rows) =>
        rows?.map((r) => (next.has(r.id) ? { ...r, tagIds: next.get(r.id)! } : r)),
      );
      if (res.skipped.length) {
        notifications.show({
          color: 'yellow',
          title: 'Nekaterih mest ni več',
          message: 'Medtem jih je nekdo izbrisal — oznake so urejene na ostalih.',
        });
      }
    },
    onError: notifyApiError,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['posts'] });
      void qc.invalidateQueries({ queryKey: ['changelog'] });
    },
  });
}

/** "Prevedi" on Mesta: the English of the instructions in the editor, as a draft. */
export function useTranslateInstructions() {
  return useMutation({
    mutationFn: ({ postId, instructions }: { postId: string; instructions: string }) =>
      api<{ html: string }>(`/posts/${postId}/translate-instructions`, {
        method: 'POST',
        body: JSON.stringify({ instructions }),
      }),
    onError: notifyApiError,
  });
}
